import React, { useState, useRef, useEffect, CSSProperties, MutableRefObject } from "react";
import "./RailcarStorage.css";
import WebMap from "@arcgis/core/WebMap";
import MapView from "@arcgis/core/views/MapView";
import Expand from "@arcgis/core/widgets/Expand";
import Search from "@arcgis/core/widgets/Search";
import GroupLayer from "@arcgis/core/layers/GroupLayer";
import LayerSearchSource from "@arcgis/core/widgets/Search/LayerSearchSource";
import Compass from "@arcgis/core/widgets/Compass";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import Basemap from "@arcgis/core/Basemap";
import Extent from "@arcgis/core/geometry/Extent";
import LayerList from "@arcgis/core/widgets/LayerList";
import CustomLegend from "../shared/widgets/custom-legend/CustomLegend";
import DetailsList from "./details-list/DetailsList";
import Filters from "./filters/Filters";
import ZoomHome from "../shared/widgets/zoom-home/ZoomHome";
import ClassRailroadVisible from "../shared/widgets/class-railroad-visible/ClassRailroadWrapper";
import { useScreenSize } from "../../context/useScreenSize";


interface iProps {
	webMapId: string;
};

export default function RailcarStorage(props: iProps) {
	const [filterByMapExtent, setFilterByMapExtent] = useState(true);
	const [stateFilterGeometry, setStateFilterGeometry] = useState<__esri.Polygon | null>(null);
	const [radiusFilterGeometry, setRadiusFilterGeometry] = useState<__esri.Polygon | null>(null);
	const [mobileMainContent, setMobileMainContent] = useState<'map' | 'details' | 'filters'>('map');
	const [stateOptions, setStateOptions] = useState<{ value: string; label: string }[]>([]);
	const stateGeometries: MutableRefObject<{ [key: string]: __esri.Geometry }> = useRef({});
	const [classIOptions, setClassIOptions] = useState<string[]>([]);

	const zoomHomeWidget = useRef<HTMLDivElement>();
	const crvWidget = useRef<HTMLDivElement>();

	const attachZoomHomeWidget = (node: HTMLDivElement | null) => {
		if (node && !zoomHomeWidget.current) {
			node.hidden = true;
			zoomHomeWidget.current = node;
		}
	};

	const attachCRVWidget = (node: HTMLDivElement | null) => {
		if (node && !crvWidget.current) {
			node.hidden = true;
			crvWidget.current = node;
		}
	};

	const mapUIInitialized = useRef(false);
	const mapDiv = useRef(null);
	const map = useRef(new WebMap({
		portalItem: {
			id: props.webMapId
		}
	}));
	const filter = useRef<React.ElementRef<typeof Filters>>(null);

	const extent = new Extent({
		xmin: -65,
		xmax: -125,
		ymax: 55,
		ymin: 25,
	});

	const view: React.MutableRefObject<__esri.MapView> = useRef(new MapView({
		map: map.current,
		extent: extent,
	}));

	if (!mapUIInitialized.current) {
		view.current.ui.remove("zoom");
		const search = new Search({
			id: 'searchbar',
			view: view.current,
			locationEnabled: false,
			includeDefaultSources: false,
			searchAllEnabled: true
		});

		view.current.when(() => {
			const statesLayer = view.current.map.layers
				.find(layer => layer.title === 'Administrative Divisions') as __esri.FeatureLayer;

			if (statesLayer) {
				statesLayer.queryFeatures({
					where: "COUNTRY = 'United States' OR NAME = 'Ontario' ",
					returnGeometry: true,
					orderByFields: ["NAME ASC"],
					outFields: ["NAME", "ISO_SUB"],
					outSpatialReference: view.current.spatialReference
				}).then(featureSet => {
					const stateOptions: { value: string; label: string }[] = [];
					featureSet.features.forEach(feature => {
						const stateAbbr = feature.attributes.ISO_SUB as string;
						stateOptions.push({
							value: stateAbbr,
							label: feature.attributes.NAME as string
						});

						stateGeometries.current[stateAbbr] = feature.geometry;
					});
					setStateOptions(stateOptions);
				});
			}

			const interchangeLayer = view.current.map.layers
				.find(layer => layer.title === 'Interchange') as __esri.FeatureLayer;

			if (interchangeLayer) {
				interchangeLayer.queryFeatures({
					where: "1=1",
					returnGeometry: false,
					orderByFields: ["Class1Code ASC"],
					outFields: ["Class1Code"],
					returnDistinctValues: true
				}).then(featureSet => {
					const codes = featureSet.features.map(feature => feature.attributes.Class1Code);
					setClassIOptions(codes);
				});
			}

			const railroadLayer = view.current.map.layers.find(layer => layer.title === 'Railroads') as GroupLayer;
			search.sources.add(new LayerSearchSource({
				exactMatch: false,
				layer: railroadLayer.layers.find(layer => layer.title === 'RailroadAreas'),
				name: 'Railroads',
				outFields: ['*'],
				searchFields: ['REFXCompanyCode', 'GPPublicName', 'GPRailroadMark']
			}));

			const terminalLayer = view.current.map.layers.find(layer => layer.title === 'Watco Operations') as GroupLayer;
			terminalLayer.layers.forEach(layer => {
				search.sources.add(new LayerSearchSource({
					exactMatch: false,
					layer: layer,
					name: layer.title,
					outFields: ['*'],
					searchFields: ['GPPublicName']
				}));
			});

			if (!search.includeDefaultSources) {
				let tempSearch = new Search();
				tempSearch.when(() => {
					search.sources.addMany(tempSearch.defaultSources);
					tempSearch.destroy();
				});
			}

			const industrialRailSites = terminalLayer.layers
				.find(layer => layer.title === "Industrial Rail Sites") as __esri.FeatureLayer
			industrialRailSites.legendEnabled = false;
			const railroadsGroupLayer = view.current.map.layers
				.find(layer => layer.title === "Railroads") as __esri.GroupLayer;
			const watcoRailroadsLayer = railroadsGroupLayer.layers
				.find(layer => layer.title === "Watco Railroads");

			const legend = new Expand({
				view: view.current,
				content: new CustomLegend({
					view: view.current,
					layerInfos: [
						{
							title: "Map Legend",
							layer: terminalLayer
						},
						{
							layer: watcoRailroadsLayer
						}
					]
				}),
				expandTooltip: "Legend",
				expanded: window.innerWidth > 1080
			});

			zoomHomeWidget.current!.hidden = false;
			if (crvWidget.current) {
				crvWidget.current.hidden = false;
			}

			view.current.ui.add([
				{
					component: zoomHomeWidget.current,
					position: "top-left",
					index: 2
				},
				{
					component: legend,
					position: "bottom-left",
					index: 0
				},
				{
					component: crvWidget.current,
					position: "bottom-right",
					index: 0
				}
			]);
		});

		const compass = new Compass({ view: view.current });

		const topRightGroup = "top-right";
		const layerList = new Expand({
			view: view.current,
			content: new LayerList({
				view: view.current,
				selectionEnabled: true
			}),
			expandTooltip: "Layer List",
			group: topRightGroup
		});

		const basemaps = new Expand({
			view: view.current,
			content: new BasemapGallery({
				view: view.current,
				source: [Basemap.fromId("gray-vector"), Basemap.fromId("hybrid"), Basemap.fromId("streets-vector"), Basemap.fromId("terrain")]
			}),
			expandTooltip: "Basemaps",
			group: topRightGroup
		});

		view.current.ui.add([
			// top left section
			{
				component: search,
				position: "top-left",
				index: 0
			},
			{
				component: compass,
				position: "top-left",
				index: 1
			},

			// top-right-section
			{
				component: layerList,
				position: "top-right",
				index: 0
			},
			{
				component: basemaps,
				position: "top-right",
				index: 1
			}

		]);

		mapUIInitialized.current = true;
	}

	useEffect(() => {
		if (mapDiv.current) {
			view.current.container = mapDiv.current as any;
		}
	}, []);

	const toggleFilterByMapExtent = () => {
		setFilterByMapExtent(!filterByMapExtent);
	}

	const showMap = () => {
		setMobileMainContent('map');
	}

	const { isSmallScreen } = useScreenSize();

	let mapStyle: CSSProperties = {};
	let detailsListStyle: CSSProperties = {};
	let mainViewStyle: CSSProperties = {};
	if (isSmallScreen) {
		if (mobileMainContent === 'map') {
			mapStyle = { width: '100%' };
			detailsListStyle = { display: 'none' };
		} else if (mobileMainContent === 'details') {
			mapStyle = { display: 'none' };
			detailsListStyle = { width: '100%' };
		} else if (mobileMainContent === 'filters') {
			mainViewStyle = { display: 'none' };
		}
	}
	return (
		<div className="railcar-storage">
			{isSmallScreen &&
				<div className="flex-row small-screen-nav">
					{mobileMainContent !== 'map' &&
						<button onClick={() => { setMobileMainContent('map') }}>Show Map</button>
					}
					{mobileMainContent !== 'filters' &&
						<button onClick={() => { setMobileMainContent('filters') }}>Show Filters</button>
					}
					{mobileMainContent !== 'details' &&
						<button onClick={() => { setMobileMainContent('details') }}>Show Details</button>
					}
				</div>
			}
			<Filters
				ref={filter}
				view={view}
				filterByMapExtent={filterByMapExtent}
				toggleFilterByMapExtent={toggleFilterByMapExtent}
				isSmallScreen={isSmallScreen}
				setStateFilterGeometry={setStateFilterGeometry}
				setRadiusFilterGeometry={setRadiusFilterGeometry}
				extent={extent}
				visible={!isSmallScreen || mobileMainContent === 'filters'}
				stateOptions={stateOptions}
				stateGeometries={stateGeometries}
				classIOptions={classIOptions}
				showMap={showMap}
			/>
			<div style={mainViewStyle} className="main-view-container">
				<div style={mapStyle} className="map-container" ref={mapDiv}>
					<ZoomHome ref={attachZoomHomeWidget} view={view.current} extent={extent} />
					<ClassRailroadVisible ref={attachCRVWidget} view={view.current} />
				</div>
				<div style={detailsListStyle} className="right-side-panel">
					<DetailsList
						view={view}
						filterByMapExtent={filterByMapExtent}
						stateFilterGeometry={stateFilterGeometry}
						filters={filter}
						stateOptions={stateOptions}
						classIOptions={classIOptions}
						radiusFilterGeometry={radiusFilterGeometry}
					/>
				</div>
			</div>
		</div>
	);
}